import { ApiClient } from '@Models';

/**
 * Caches the Activity CodeName -> DisplayName map and resolves the label to show on the UI.
 * The underlying code (CodeName) stays the value used for selection/filtering/saving;
 * only the visible label is replaced with DisplayName when one is configured.
 */
export class ActivityDisplayNameService {
	private _apiClient: ApiClient | null = null;
	private _map: { [code: string]: string } = {};
	private _loadPromise: Promise<void> | null = null;

	/**
	 * Idempotent: loads the map once and reuses the same promise on subsequent calls.
	 * Callers that render activity labels should await this first (the picker data loaders do).
	 */
	public async load(): Promise<void> {
		this._loadPromise ??= this._loadMap();
		await this._loadPromise;
	}

	private async _loadMap(): Promise<void> {
		// Create the ApiClient lazily (not at module load) to avoid a circular
		// import: @Services/index -> this service -> @Models -> @Services.
		this._apiClient ??= new ApiClient();
		const { data: { map } } = await this._apiClient.ruleActivityDisplayNamesGet();
		this._map = map ?? {};
	}

	/** Returns the DisplayName for a code when configured, otherwise the code itself. */
	public getDisplayName(code: string | undefined): string {
		if (!code) return '';
		return this._map[code] ?? code;
	}
}

export const activityDisplayNameService = new ActivityDisplayNameService();
